import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { ArrowLeft, Save, Eye, Plus, Trash2, Edit, GripVertical } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const VisualBuilder = () => {
  const { websiteId } = useParams();
  const navigate = useNavigate();
  const [website, setWebsite] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [pageStructure, setPageStructure] = useState([]);
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchWebsite();
    fetchWidgets();
  }, [websiteId]);

  const fetchWebsite = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/websites/${websiteId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setWebsite(data);
      setPageStructure(data.page_structure || []);
    } catch (error) {
      toast.error('Failed to load website');
    }
  };

  const fetchWidgets = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/widgets`);
      const data = await response.json();
      const widgetArray = Object.entries(data.widgets || {}).map(([key, value]) => ({
        id: key,
        ...value
      }));
      setWidgets(widgetArray);
    } catch (error) {
      toast.error('Failed to load widgets');
    }
  };

  const addWidget = (widgetType) => {
    const widget = widgets.find(w => w.id === widgetType);
    if (!widget) return;

    // Create default config from schema
    const defaultConfig = {};
    Object.entries(widget.schema || {}).forEach(([key, field]) => {
      defaultConfig[key] = field.default;
    });

    const newWidget = {
      id: `${widgetType}-${Date.now()}`,
      type: widgetType,
      config: defaultConfig
    };

    setPageStructure([...pageStructure, newWidget]);
    setSelectedWidget(newWidget);
    toast.success(`${widget.name} added!`);
  };

  const deleteWidget = (widgetId) => {
    setPageStructure(pageStructure.filter(w => w.id !== widgetId));
    if (selectedWidget?.id === widgetId) {
      setSelectedWidget(null);
    }
    toast.success('Widget removed');
  };

  const updateWidgetConfig = (widgetId, key, value) => {
    setPageStructure(pageStructure.map(w => 
      w.id === widgetId 
        ? { ...w, config: { ...w.config, [key]: value } }
        : w
    ));
    
    // Update selected widget
    if (selectedWidget?.id === widgetId) {
      setSelectedWidget({
        ...selectedWidget,
        config: { ...selectedWidget.config, [key]: value }
      });
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(pageStructure);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setPageStructure(items);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/websites/${websiteId}/page-structure`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ page_structure: pageStructure })
      });
      toast.success('Website saved successfully!');
      fetchWebsite();
    } catch (error) {
      toast.error('Failed to save website');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    // Save first
    await handleSave();
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/websites/${websiteId}/render`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to render');
      }
      
      const data = await response.json();
      
      if (!data.html || data.html.length < 200) {
        // If no proper HTML, generate from website data
        const websiteResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/websites/${websiteId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const websiteData = await websiteResponse.json();
        
        // Create basic preview from website data
        const basicPreview = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${websiteData.name}</title>
  <style>${websiteData.css_code || 'body { font-family: Arial, sans-serif; padding: 20px; }'}</style>
</head>
<body>
  ${websiteData.html_code || '<h1>' + websiteData.name + '</h1><p>Start adding widgets to build your page!</p>'}
  <script>${websiteData.js_code || ''}</script>
</body>
</html>`;
        
        setPreviewHtml(basicPreview);
      } else {
        setPreviewHtml(data.html);
      }
      
      setPreviewMode(true);
    } catch (error) {
      toast.error('Failed to generate preview');
      console.error('Preview error:', error);
    }
  };

  const getWidgetInfo = (widgetId) => {
    return widgets.find(w => w.id === widgetId);
  };

  if (previewMode) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="bg-zinc-900 border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <h1 className="text-xl font-bold" data-testid="preview-title">Preview: {website?.name}</h1>
          <Button
            onClick={() => setPreviewMode(false)}
            data-testid="close-preview-button"
            className="bg-white text-black hover:bg-zinc-200"
          >
            Close Preview
          </Button>
        </div>
        <iframe
          srcDoc={previewHtml}
          className="flex-1 w-full border-0"
          title="Website Preview"
          data-testid="preview-iframe"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="visual-builder-page">
      {/* Widget Sidebar */}
      <div className="w-80 bg-zinc-900 border-r border-white/10 overflow-y-auto">
        <div className="p-6">
          <Button
            onClick={() => navigate('/dashboard')}
            data-testid="back-button"
            variant="ghost"
            className="text-zinc-400 hover:text-white mb-6 w-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <h2 className="text-xl font-bold mb-4">Widgets</h2>
          <p className="text-sm text-zinc-400 mb-6">Click to add to your page</p>

          <div className="space-y-2">
            {widgets.map((widget) => (
              <button
                key={widget.id}
                onClick={() => addWidget(widget.id)}
                data-testid={`add-widget-${widget.id}`}
                className="w-full text-left p-4 bg-zinc-800 hover:bg-zinc-700 rounded-xl transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{widget.icon}</span>
                  <div>
                    <div className="font-medium">{widget.name}</div>
                    <div className="text-xs text-zinc-400 capitalize">{widget.category}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Canvas Area */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <div className="bg-zinc-900 border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold" data-testid="website-name">{website?.name}</h1>
            <p className="text-sm text-zinc-400">{pageStructure.length} widgets</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={handlePreview}
              data-testid="preview-button"
              variant="outline"
              className="border-zinc-700 text-white hover:border-zinc-600"
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-button"
              className="bg-violet-500 hover:bg-violet-600 text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 overflow-y-auto bg-zinc-950 p-6">
          <div className="max-w-5xl mx-auto">
            {pageStructure.length === 0 && website?.html_code ? (
              /* Show template content when no widgets but template HTML exists */
              <div className="space-y-4" data-testid="template-canvas">
                <div className="bg-zinc-900 border border-white/10 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">📄</span>
                      <div>
                        <div className="font-bold">Template Content</div>
                        <div className="text-xs text-zinc-400">From your selected template</div>
                      </div>
                    </div>
                    <span className="text-xs text-violet-400 bg-violet-500/20 px-2 py-1 rounded">Editable</span>
                  </div>
                  <div className="bg-white rounded-lg overflow-hidden" style={{ minHeight: '400px' }}>
                    <iframe
                      srcDoc={`<!DOCTYPE html>
<html>
<head>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    ${website.css_code || ''}
  </style>
</head>
<body>
  ${website.html_code}
  <script>${website.js_code || ''}</script>
</body>
</html>`}
                      className="w-full h-96 border-0"
                      title="Template Preview"
                      data-testid="template-preview-iframe"
                    />
                  </div>
                </div>
                <p className="text-center text-zinc-500 text-sm">
                  Add widgets from the sidebar to customize your page, or click Preview to see the full template
                </p>
              </div>
            ) : pageStructure.length === 0 ? (
              <div className="text-center py-20 glass rounded-3xl" data-testid="empty-canvas">
                <Plus className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-2">Start Building</h3>
                <p className="text-zinc-400">Click widgets from the sidebar to add them to your page</p>
              </div>
            ) : (
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="widgets">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="space-y-4"
                    >
                      {pageStructure.map((widget, index) => {
                        const widgetInfo = getWidgetInfo(widget.type);
                        return (
                          <Draggable key={widget.id} draggableId={widget.id} index={index}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`bg-zinc-900 border-2 rounded-xl p-6 transition-all ${
                                  selectedWidget?.id === widget.id
                                    ? 'border-violet-500'
                                    : 'border-white/10 hover:border-white/20'
                                }`}
                                data-testid={`widget-${widget.id}`}
                              >
                                <div className="flex items-center justify-between mb-4">
                                  <div className="flex items-center gap-3">
                                    <div {...provided.dragHandleProps}>
                                      <GripVertical className="w-5 h-5 text-zinc-500 cursor-grab" />
                                    </div>
                                    <span className="text-2xl">{widgetInfo?.icon}</span>
                                    <div>
                                      <div className="font-bold">{widgetInfo?.name}</div>
                                      <div className="text-xs text-zinc-400">{widget.type}</div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      onClick={() => setSelectedWidget(widget)}
                                      data-testid={`edit-widget-${widget.id}`}
                                      size="sm"
                                      variant="outline"
                                      className="border-zinc-700"
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      onClick={() => deleteWidget(widget.id)}
                                      data-testid={`delete-widget-${widget.id}`}
                                      size="sm"
                                      variant="outline"
                                      className="border-red-800 text-red-400 hover:bg-red-900/20"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                                
                                {/* Widget Preview */}
                                <div className="bg-zinc-800 rounded-lg p-4 text-sm text-zinc-400">
                                  <div className="grid grid-cols-2 gap-2">
                                    {Object.entries(widget.config || {}).slice(0, 4).map(([key, value]) => (
                                      <div key={key}>
                                        <span className="text-zinc-500 text-xs">{key}:</span>
                                        <div className="text-white truncate">{String(value).slice(0, 30)}</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        </div>
      </div>

      {/* Property Panel */}
      {selectedWidget && (
        <div className="w-96 bg-zinc-900 border-l border-white/10 overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Edit Widget</h2>
              <Button
                onClick={() => setSelectedWidget(null)}
                data-testid="close-properties"
                variant="ghost"
                size="sm"
                className="text-zinc-400"
              >
                ✕
              </Button>
            </div>

            <div className="space-y-6">
              {Object.entries(getWidgetInfo(selectedWidget.type)?.schema || {}).map(([key, field]) => (
                <div key={key}>
                  <label className="block text-sm font-medium mb-2">{field.label}</label>
                  
                  {field.type === 'text' && (
                    <input
                      type="text"
                      value={selectedWidget.config[key] || ''}
                      onChange={(e) => updateWidgetConfig(selectedWidget.id, key, e.target.value)}
                      data-testid={`input-${key}`}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-violet-500 focus:outline-none"
                    />
                  )}
                  
                  {field.type === 'textarea' && (
                    <textarea
                      value={selectedWidget.config[key] || ''}
                      onChange={(e) => updateWidgetConfig(selectedWidget.id, key, e.target.value)}
                      data-testid={`textarea-${key}`}
                      rows={4}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-violet-500 focus:outline-none resize-none"
                    />
                  )}
                  
                  {field.type === 'color' && (
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={selectedWidget.config[key] || field.default}
                        onChange={(e) => updateWidgetConfig(selectedWidget.id, key, e.target.value)}
                        data-testid={`color-${key}`}
                        className="h-10 w-20 cursor-pointer rounded"
                      />
                      <input
                        type="text"
                        value={selectedWidget.config[key] || field.default}
                        onChange={(e) => updateWidgetConfig(selectedWidget.id, key, e.target.value)}
                        className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-violet-500 focus:outline-none"
                      />
                    </div>
                  )}
                  
                  {field.type === 'boolean' && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedWidget.config[key] || false}
                        onChange={(e) => updateWidgetConfig(selectedWidget.id, key, e.target.checked)}
                        data-testid={`checkbox-${key}`}
                        className="w-5 h-5"
                      />
                      <span className="text-zinc-400">Enable</span>
                    </label>
                  )}
                  
                  {field.type === 'select' && (
                    <select
                      value={selectedWidget.config[key] || field.default}
                      onChange={(e) => updateWidgetConfig(selectedWidget.id, key, e.target.value)}
                      data-testid={`select-${key}`}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-violet-500 focus:outline-none"
                    >
                      {field.options?.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  )}
                  
                  {field.type === 'image' && (
                    <input
                      type="url"
                      value={selectedWidget.config[key] || ''}
                      onChange={(e) => updateWidgetConfig(selectedWidget.id, key, e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      data-testid={`image-${key}`}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-violet-500 focus:outline-none"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisualBuilder;
